import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Users, Check, Mail } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GuestJoinPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const inviteToken = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [guestInfo, setGuestInfo] = useState({
    first_name: '',
    last_name: '',
    email: ''
  });
  const [joined, setJoined] = useState(false);
  const [subscribeNewsletter, setSubscribeNewsletter] = useState(false);

  useEffect(() => {
    if (inviteToken) {
      validateInvite();
    } else {
      toast.error('Invalid invite link');
      navigate('/');
    }
  }, [inviteToken]);

  const validateInvite = async () => {
    try {
      const response = await axios.get(`${API}/group/invite/${inviteToken}`);
      setInviteData(response.data);
      
      // Pre-fill email if provided in invite
      if (response.data.guest_email) {
        setGuestInfo(prev => ({ ...prev, email: response.data.guest_email }));
      }
    } catch (error) {
      toast.error('Invalid or expired invite');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const joinTour = async () => {
    if (!guestInfo.first_name || !guestInfo.last_name) {
      toast.error('Please enter your first and last name');
      return;
    }

    if (!guestInfo.email) {
      toast.error('Please enter your email');
      return;
    }

    setJoining(true);
    try {
      const response = await axios.post(`${API}/group/join/${inviteToken}`, guestInfo);
      
      // Store guest token for tour access
      localStorage.setItem('guest_token', response.data.guest_token);
      localStorage.setItem('guest_name', `${guestInfo.first_name} ${guestInfo.last_name}`);
      
      setJoined(true);
      toast.success(`Welcome to ${inviteData.host_name}'s tour!`);
      
      // Subscribe to newsletter if opted in
      if (subscribeNewsletter && guestInfo.email) {
        try {
          await axios.post(`${API}/newsletter/subscribe`, {
            email: guestInfo.email,
            name: `${guestInfo.first_name} ${guestInfo.last_name}`.trim()
          });
        } catch (e) {
          // Silent fail — don't block tour join
        }
      }
      
      // Redirect to tour player after 2 seconds
      setTimeout(() => {
        window.location.href = response.data.tour_url;
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join tour');
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-cyan-500 border-t-transparent"></div>
      </div>
    );
  }

  if (joined) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-white border-4 border-green-500 rounded-2xl shadow-card p-8 text-center">
            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-3xl font-black uppercase text-gray-900 mb-4">You're In!</h2>
            <p className="text-lg text-gray-700 mb-2">
              Welcome to <strong>{inviteData?.host_name}'s</strong> tour
            </p>
            <p className="text-gray-600">
              Redirecting to tour...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-white border-4 border-cyan-500 rounded-2xl shadow-card p-8">
          <div className="flex items-center justify-center mb-6">
            <Users className="w-16 h-16 text-cyan-500" />
          </div>
          
          <h1 className="text-3xl font-black uppercase text-center text-gray-900 mb-2">
            Join the Tour!
          </h1>
          <p className="text-center text-lg text-gray-700 mb-6">
            <strong>{inviteData?.host_name}</strong> invited you to:
          </p>
          <div className="bg-cyan-50 border-2 border-cyan-300 rounded-xl p-4 mb-6 text-center">
            <p className="text-xl font-bold text-gray-900">{inviteData?.tour_name}</p>
            <p className="text-gray-600">{inviteData?.tour_city}</p>
          </div>

          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                First Name *
              </label>
              <input
                type="text"
                value={guestInfo.first_name}
                onChange={(e) => setGuestInfo({...guestInfo, first_name: e.target.value})}
                data-testid="guest-first-name"
                className="w-full px-4 py-3 rounded-lg border-2 border-cyan-300 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition-all text-lg"
                placeholder="John"
              />
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Last Name *
              </label>
              <input
                type="text"
                value={guestInfo.last_name}
                onChange={(e) => setGuestInfo({...guestInfo, last_name: e.target.value})}
                data-testid="guest-last-name"
                className="w-full px-4 py-3 rounded-lg border-2 border-cyan-300 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition-all text-lg"
                placeholder="Smith"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Email *
              </label>
              <input
                type="email"
                value={guestInfo.email}
                onChange={(e) => setGuestInfo({...guestInfo, email: e.target.value})}
                data-testid="guest-email"
                className="w-full px-4 py-3 rounded-lg border-2 border-cyan-300 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition-all text-lg"
                placeholder="john@example.com"
              />
            </div>
          </div>

          {/* Newsletter Opt-in */}
          <label
            className="flex items-start gap-3 p-4 bg-teal-50 border-2 border-teal-200 rounded-xl cursor-pointer hover:bg-teal-100 transition-colors mb-6"
            data-testid="guest-newsletter-optin"
          >
            <input
              type="checkbox"
              checked={subscribeNewsletter}
              onChange={(e) => setSubscribeNewsletter(e.target.checked)}
              className="mt-1 w-5 h-5 rounded border-teal-400 text-teal-500 focus:ring-teal-500"
            />
            <div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-teal-600" />
                <span className="font-semibold text-gray-900 text-sm">Get the inside scoop</span>
              </div>
              <p className="text-gray-600 text-xs mt-1">
                Be the first to hear about new tours, insider travel tips & exclusive deals.
              </p>
            </div>
          </label>

          <button
            onClick={joinTour}
            disabled={joining}
            data-testid="join-tour-button"
            className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white py-4 rounded-full font-black text-xl uppercase shadow-xl hover:shadow-2xl transition-all active:scale-95 disabled:cursor-not-allowed"
          >
            {joining ? 'Joining...' : 'Join Tour'}
          </button>

          <p className="text-sm text-gray-600 text-center mt-4">
            No app download required! Just enter your info and you're in.
          </p>
        </div>
      </div>
    </div>
  );
};

export default GuestJoinPage;